import {Locale} from '@aboutyou/config';
import {useFormatCurrency, useLocale} from '@aboutyou/i18n';
import {useMemo} from 'react';
import {useScayleConfig} from './useScayleConfig';
import {useScaylePaymentMethodState} from './useScaylePaymentMethodState';
import {useScayleState} from './useScayleState';

const HOME_DELIVERY_SERVICE_FEE_KEY = 'home_delivery_service_fee';

// Baltic shops display the ISO code ("EUR") instead of "€" for the
// home delivery fee to match the checkout price breakdown formatting
const ISO_CURRENCY_SIGN_LOCALES: Locale[] = [
  Locale.lv_LV,
  Locale.ru_LV,
  Locale.lt_LT,
];

export interface HomeDeliveryFee {
  isHomeDeliveryFeeEligible: boolean;
  homeDeliveryFee: string | null;
  isHomeDeliverySelected: boolean;
  movValue: number | undefined;
  orderValue: number | undefined;
  formattedMOV: string | null;
}

export const useScayleShippingState = (): HomeDeliveryFee => {
  const formatPrice = useFormatCurrency();
  const locale = useLocale();

  const {data: scayleState} = useScayleState();
  const {data: scayleConfig} = useScayleConfig();

  const {selectedShippingOption} = useScaylePaymentMethodState();

  const serviceCosts = scayleState?.orderOptions?.conditionalFees;
  const currency = scayleConfig?.currency;

  return useMemo(() => {
    const homeDeliveryFeeRaw = serviceCosts?.find(
      cost => cost.key === HOME_DELIVERY_SERVICE_FEE_KEY,
    )?.amountWithTax;

    const movValue =
      selectedShippingOption?.orderValueLimits?.maxBruttoOrderValue ||
      selectedShippingOption?.orderValueLimits?.minBruttoOrderValue;

    const orderValue =
      scayleState?.orderState?.basket?.total
        ?.costWithTaxWithMembershipDiscountWithoutServiceCosts;

    const isHomeDeliveryFeeEligible =
      homeDeliveryFeeRaw !== undefined && homeDeliveryFeeRaw > 0;

    const isIsoCurrencySignLocale = ISO_CURRENCY_SIGN_LOCALES.includes(locale);

    const homeDeliveryFee =
      isHomeDeliveryFeeEligible && homeDeliveryFeeRaw !== undefined && currency
        ? formatPrice(homeDeliveryFeeRaw, currency, {
            useIsoSign: isIsoCurrencySignLocale,
          })
        : null;

    const formattedMOV =
      movValue !== undefined && currency
        ? formatPrice(movValue, currency, {
            useIsoSign: isIsoCurrencySignLocale,
          })
        : null;

    return {
      isHomeDeliveryFeeEligible,
      homeDeliveryFee,
      isHomeDeliverySelected:
        selectedShippingOption?.deliveryOptionKey === 'home_delivery',
      movValue,
      orderValue,
      formattedMOV,
    };
  }, [
    serviceCosts,
    selectedShippingOption?.orderValueLimits?.maxBruttoOrderValue,
    selectedShippingOption?.orderValueLimits?.minBruttoOrderValue,
    selectedShippingOption?.deliveryOptionKey,
    scayleState?.orderState?.basket?.total
      ?.costWithTaxWithMembershipDiscountWithoutServiceCosts,
    currency,
    formatPrice,
    locale,
  ]);
};
